import { makeStyles } from "tss-react/mui";

export type ActivityStatusState = "ready" | "waiting" | "blocked";

const useStyles = makeStyles<{ background?: string }>()((theme) => ({
  body: {
    marginTop: -64,
    minHeight: "100vh",
    padding: "104px 0 96px",
    background: theme.design.color.surface.canvas,
    color: theme.design.color.text.primary,
    [theme.breakpoints.down("sm")]: {
      marginTop: -32,
      paddingTop: 72,
      paddingBottom: 48,
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 22,
    width: "100%",
    maxWidth: 1540,
    margin: "auto",
    padding: "0 32px",
    boxSizing: "border-box",
    "& > *": { minWidth: 0, boxSizing: "border-box" },
    [theme.breakpoints.down("lg")]: { padding: "0 24px" },
    [theme.breakpoints.down("sm")]: { padding: "0 12px", gap: 16 },
  },
  containerRail: {
    display: "grid",
    gridTemplateColumns: "minmax(230px, 280px) minmax(0, 1fr)",
    alignItems: "start",
    gap: 22,
    [theme.breakpoints.down("lg")]: { display: "flex", flexDirection: "column" },
  },
  workspace: {
    display: "flex",
    flexDirection: "column",
    gap: 22,
    minWidth: 0,
    width: "100%",
    "& > *": { minWidth: 0 },
    [theme.breakpoints.down("sm")]: { gap: 16 },
  },
  header: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    minHeight: 174,
    padding: "28px 32px",
    border: `1px solid ${theme.design.color.border.black}`,
    borderRadius: theme.design.radius.xl,
    backgroundColor: theme.design.color.surface.container,
    backgroundImage: `linear-gradient(90deg, ${theme.design.color.surface.container} 0%, ${theme.design.color.surface.container}ee 43%, ${theme.design.color.surface.container}77 100%), var(--activity-scene)`,
    backgroundSize: "100% 100%, contain",
    backgroundPosition: "right top",
    backgroundRepeat: "no-repeat",
    boxShadow: theme.design.shadow.sm,
    boxSizing: "border-box",
    '[data-layout="rail"] &': {
      position: "sticky",
      top: 88,
      minHeight: 520,
      alignItems: "stretch",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "28px 24px",
      backgroundImage: `linear-gradient(180deg, ${theme.design.color.surface.container}ee 0%, ${theme.design.color.surface.container}99 42%, ${theme.design.color.surface.container} 100%), var(--activity-scene)`,
      backgroundPosition: "right top",
    },
    [theme.breakpoints.down("lg")]: {
      width: "100%",
      '[data-layout="rail"] &': { position: "relative", top: "auto", minHeight: 174, flexDirection: "row", alignItems: "center" },
    },
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "center",
      '[data-layout="rail"] &': { flexDirection: "column", alignItems: "stretch" },
    },
    [theme.breakpoints.down("sm")]: {
      padding: "22px 20px",
      gap: 18,
      '[data-layout="rail"] &': { minHeight: 0 },
    },
  },
  titleBlock: {
    display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, minWidth: 0, maxWidth: 780,
    '[data-layout="rail"] &': { maxWidth: "100%" },
  },
  kicker: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 11,
    lineHeight: 1.3,
    fontWeight: 600,
    letterSpacing: ".19em",
    textTransform: "uppercase",
    color: `var(--activity-accent, ${theme.design.color.status.warning})`,
  },
  titleRow: { display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "8px 20px" },
  titleIcon: { width: 38, height: 38, objectFit: "contain", alignSelf: "center" },
  title: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: "clamp(2.6rem, 4.5vw, 4.6rem)",
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: ".005em",
    textTransform: "none",
    color: theme.design.color.text.primary,
    textShadow: theme.design.shadow.medallionText,
    margin: 0,
    '[data-layout="rail"] &': { fontSize: "clamp(2.4rem, 3.2vw, 3.4rem)", overflowWrap: "anywhere" },
    [theme.breakpoints.down("lg")]: { '[data-layout="rail"] &': { fontSize: "clamp(2.6rem, 4.5vw, 4.6rem)" } },
    [theme.breakpoints.down("sm")]: { fontSize: "clamp(2.4rem, 10vw, 3.8rem)" },
  },
  description: {
    maxWidth: 580,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 16,
    lineHeight: 1.5,
    color: theme.design.color.text.muted,
    textShadow: theme.design.shadow.sm,
  },
  city: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 0",
    borderBottom: `1px solid var(--activity-accent, ${theme.design.color.status.warning})`,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: theme.design.color.text.muted,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    maxWidth: 450,
    '[data-layout="rail"] &': { alignItems: "flex-start", justifyContent: "flex-start", flexDirection: "column" },
    [theme.breakpoints.down("lg")]: { '[data-layout="rail"] &': { flexDirection: "row", alignItems: "center", justifyContent: "flex-end" } },
    [theme.breakpoints.down("md")]: { '[data-layout="rail"] &': { flexDirection: "row", justifyContent: "flex-start" } },
    [theme.breakpoints.down("md")]: { justifyContent: "flex-start", maxWidth: "100%" },
  },
  meta: { fontFamily: theme.design.typography.fontFamily, fontSize: 12, color: theme.design.color.text.muted, fontVariantNumeric: "tabular-nums" },
  status: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "10px 14px",
    border: `1px solid var(--activity-accent, ${theme.design.color.status.warning})`,
    borderRadius: theme.design.radius.pill,
    background: theme.design.color.surface.control,
    color: theme.design.color.text.primary,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 12,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "normal",
  },
  dot: { width: 8, height: 8, borderRadius: theme.design.radius.pill, flexShrink: 0 },
  dotReady: { backgroundColor: theme.design.color.status.success, boxShadow: theme.design.shadow.successGlow },
  dotWaiting: { backgroundColor: theme.design.color.status.warning },
  dotBlocked: { backgroundColor: theme.design.color.status.danger },
  perk: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    maxWidth: 280,
    padding: "9px 12px",
    background: theme.design.color.surface.control,
    border: `1px solid ${theme.design.color.border.default}`,
    borderRadius: theme.design.radius.pill,
    color: theme.design.color.text.primary,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 11,
    lineHeight: 1.2,
    fontWeight: 600,
    "& img": { width: 16, height: 16, objectFit: "contain" },
  },
}));

export default useStyles;
